import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { SseService } from 'src/app/services/SseService';
import { RealTimeService } from 'src/app/services/realtime.service';
import { OAuthService } from 'src/app/services/oauth.service';
import { BrokerAdapterFactory } from 'src/app/services/broker-adapter-factory.service';
import { MetaApiService } from 'src/app/services/meta-api.service';
import { MetaApiStreamingService } from 'src/app/services/metaapi-streaming.service';
import { fechaHoyIso, isoADisplay } from 'src/app/shared/utils/date-utils';

export type FuenteCotizacion = 'API' | 'IBKR' | 'METAAPI';

export interface CotizacionPrecio {
  last: number;
  bid: number | null;
  ask: number | null;
  fuente: FuenteCotizacion | 'HISTORICO';
}

const ETIQUETAS: Record<FuenteCotizacion, string> = { API: 'API', IBKR: 'IBKR', METAAPI: 'MetaTrader' };

/**
 * Selector de fuente + cotización de los diálogos de Operación de Entrada / Salida.
 * Fecha de hoy: tiempo real de la fuente elegida (API propia, IBKR o MetaTrader vía MetaAPI);
 * otra fecha, o tiempo real sin datos: cotización histórica (obtenerCotizacion).
 */
@Component({
  selector: 'app-cotizacion-fuente',
  templateUrl: './cotizacion-fuente.component.html',
  styleUrl: './cotizacion-fuente.component.scss'
})
export class CotizacionFuenteComponent implements OnChanges, OnDestroy {
  @Input() wallet: any;
  @Input() isBroker = false;
  @Input() idNectum: number;
  /** Símbolo para la API propia y MetaAPI (iso, o nombreCompania sin ' CFD'). */
  @Input() symbol: string;
  @Input() conid: number;
  /** yyyy-MM-dd */
  @Input() fecha: string;
  @Input() decimales = 2;
  @Input() descripcion: string;
  @Input() stkcfd: string;
  /** Muestra STK/CFD como toggle (en Broker) en lugar del tag de solo lectura. */
  @Input() tipoEditable = false;
  @Output() stkcfdChange = new EventEmitter<string>();

  /** Precio nuevo (solo cuando cambia el último). */
  @Output() precio = new EventEmitter<CotizacionPrecio>();
  /** true si la fecha no tiene cotización (bloquea Aceptar). */
  @Output() sinDatos = new EventEmitter<boolean>();
  /** Moneda y bolsa del contrato (IBKR secdef). */
  @Output() contrato = new EventEmitter<{ currency: string; listingExchange: string }>();

  fuente: FuenteCotizacion = 'API';
  cotizacion: { symbol: string; bid: string; last: string; ask: string } | null = null;
  currency = '';
  listingExchange = '';
  readonly etiquetas = ETIQUETAS;

  private subs = new Subscription();
  private ultimoLast: number | null = null;
  private ultimoSinDatos: boolean | null = null;
  private historicoProbado = false;

  constructor(
    private sse: SseService,
    private realtime: RealTimeService,
    private oauth: OAuthService,
    private brokerAdapterFactory: BrokerAdapterFactory,
    private metaApiService: MetaApiService,
    private metaApiStreaming: MetaApiStreamingService,
  ) { }

  get esMetaApi(): boolean {
    return this.wallet?.tipoBroker === 'METAAPI';
  }

  /** API siempre; además la del broker de la cartera (solo en Broker). */
  get fuentes(): FuenteCotizacion[] {
    if (!this.isBroker) return ['API'];
    return ['API', this.esMetaApi ? 'METAAPI' : 'IBKR'];
  }

  get infoContrato(): string {
    return [this.descripcion, this.currency, this.listingExchange].filter(Boolean).join(' - ');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['wallet'] || changes['isBroker']) && (changes['wallet']?.firstChange || !this.fuentes.includes(this.fuente))) {
      this.fuente = this.isBroker && this.esMetaApi ? 'METAAPI' : 'API';
    }
    if (changes['symbol'] || changes['idNectum'] || changes['conid']) {
      this.currency = '';
      this.listingExchange = '';
    }
    this.cargar();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  cambiarFuente(): void {
    this.cargar();
  }

  private cargar(): void {
    this.subs.unsubscribe();
    this.subs = new Subscription();
    this.ultimoLast = null;
    this.historicoProbado = false;
    this.cotizacion = null;
    if (!this.idNectum && !this.symbol) return;

    if (this.fecha !== fechaHoyIso()) {
      this.historico();
    } else if (this.fuente === 'METAAPI') {
      this.tiempoRealMetaApi();
    } else if (this.fuente === 'IBKR') {
      this.tiempoRealIbkr();
    } else {
      this.tiempoRealApi();
    }
  }

  private historico(): void {
    const url = 'obtenerCotizacion?idnectum=' + this.idNectum + '&fecha=' + isoADisplay(this.fecha);
    this.subs.add(this.sse.apiGetNew(url).subscribe({
      next: (datos: any) => {
        const precio = Number(datos?.precio);
        if (!precio) {
          this.mostrar('No Data', 'No Data', 'No Data');
          this.emitirSinDatos(true);
          return;
        }
        this.mostrar(null, precio, null);
        this.emitirPrecio({ last: precio, bid: null, ask: null, fuente: 'HISTORICO' });
      },
      error: (err: any) => {
        console.error('Error al obtener la cotización histórica:', err);
        this.emitirSinDatos(true);
      }
    }));
  }

  private tiempoRealApi(): void {
    const url = 'v3/stock/full/real-time-price/' + encodeURIComponent(this.symbol);
    this.subs.add(this.realtime.fetchDataEverySecond(url).subscribe({
      next: (data: any) => {
        const dato = Array.isArray(data) ? data[0] : null;
        const last = Number(dato?.lastSalePrice);
        if (!last) {
          this.sinTiempoReal();
          return;
        }
        this.aplicarTiempoReal(last, Number(dato.bidPrice), Number(dato.askPrice), 'API', dato.symbol);
      },
      // El polling muere con el primer error: se queda con la histórica
      error: () => this.historico()
    }));
  }

  private tiempoRealIbkr(): void {
    const url = '/iserver/marketdata/snapshot?conids=' + this.conid + '&fields=31,7059,84,88,86,85';
    this.subs.add(this.oauth.fetchDataEverySecoundsbrk(url, 1000).subscribe({
      next: (data: any) => {
        const dato = Array.isArray(data) ? data[0] : null;
        const last = Number(String(dato?.['31'] ?? '').replace(/[A-Z]/g, ''));
        if (!last) {
          this.sinTiempoReal();
          return;
        }
        this.aplicarTiempoReal(last, Number(dato['84']), Number(dato['86']), 'IBKR');
      },
      error: () => this.historico()
    }));
    this.subs.add(this.oauth.apiGetIbrk('/trsrv/secdef?conids=' + this.conid).subscribe({
      next: (data: any) => {
        const secdef = data?.secdef?.[0];
        if (!secdef) return;
        this.currency = secdef.currency;
        this.listingExchange = secdef.listingExchange;
        this.contrato.emit({ currency: secdef.currency, listingExchange: secdef.listingExchange });
      }
    }));
  }

  /**
   * Socket de MetaAPI (el broker ya fija la cuenta de la cartera); mientras llega el primer tick
   * se usa el último precio del terminal o el REST current-price. MetaAPI no da "last": se usa el bid.
   */
  private tiempoRealMetaApi(): void {
    const accountId = this.brokerAdapterFactory.getAccountId(this.wallet);
    const aplicar = (p: any) => {
      const bid = Number(p?.bid ?? p?.ask);
      const ask = Number(p?.ask ?? p?.bid);
      if (bid) this.aplicarTiempoReal(bid, bid, ask, 'METAAPI', p.symbol);
    };
    this.metaApiStreaming.useAccount(accountId);

    const ultimo = this.metaApiStreaming.lastPrice(this.symbol);
    if (ultimo) {
      // Diferido: emitir dentro de ngOnChanges cambiaría el padre en mitad de su detección de cambios
      queueMicrotask(() => aplicar(ultimo));
    } else {
      this.subs.add(this.metaApiService.getCurrentPrice(accountId, this.symbol).subscribe(p => {
        if (p) aplicar(p);
        else if (this.ultimoLast === null) this.historico();
      }));
    }
    this.subs.add(this.metaApiStreaming.prices$([this.symbol], 1000).subscribe(aplicar));
  }

  /** Tiempo real sin precio: se prueba una vez la histórica antes de marcar "sin datos". */
  private sinTiempoReal(): void {
    if (this.ultimoLast !== null || this.historicoProbado) return;
    this.historicoProbado = true;
    this.historico();
  }

  private aplicarTiempoReal(last: number, bid: number, ask: number, fuente: FuenteCotizacion, symbol?: string): void {
    this.mostrar(bid, last, ask, symbol);
    this.emitirPrecio({ last, bid: bid || null, ask: ask || null, fuente });
  }

  private mostrar(bid: number | string | null, last: number | string, ask: number | string | null, symbol?: string): void {
    const fmt = (v: number | string | null) => v === null ? '—' : typeof v === 'number' && !isNaN(v) ? v.toFixed(this.decimales) : String(v);
    this.cotizacion = { symbol: symbol || this.symbol, bid: fmt(bid), last: fmt(last), ask: fmt(ask) };
  }

  private emitirPrecio(precio: CotizacionPrecio): void {
    this.emitirSinDatos(false);
    const last = Number(precio.last.toFixed(this.decimales));
    if (last === this.ultimoLast) return;
    this.ultimoLast = last;
    this.precio.emit({ ...precio, last });
  }

  private emitirSinDatos(valor: boolean): void {
    if (valor === this.ultimoSinDatos) return;
    this.ultimoSinDatos = valor;
    this.sinDatos.emit(valor);
  }
}
